import { LoadingIndicator } from '@vrplatform/components-legacy';
import { Autocomplete } from '@mantine/core';
import type { EmotionSx } from '@mantine/emotion';
import { useDebouncedValue, useDidUpdate } from '@mantine/hooks';
import {
  type ComponentProps,
  type ReactNode,
  forwardRef,
  useEffect,
  useState,
} from 'react';

interface Props extends ComponentProps<typeof Autocomplete> {
  loadingQuery?: boolean;
  loadingMutation?: boolean;
  setValue?: (v: string) => void;
  debounce?: number;
  placeholder?: string;
  leftSection?: ReactNode;
  value?: string;
  width?: string | number;
  error?: string | boolean;
  name?: string;
  sx?: EmotionSx;
}

export const InputAutocomplete = forwardRef<HTMLInputElement, Props>(
  (
    {
      setValue,
      placeholder,
      loadingMutation,
      loadingQuery,
      debounce = 500,
      leftSection,
      value,
      sx,
      ...props
    },
    ref
  ) => {
    const [input, setInput] = useState(value);
    const [delayed] = useDebouncedValue(input, debounce);

    useDidUpdate(() => {
      if (typeof delayed === 'string' && setValue) {
        setValue(delayed);
      }
    }, [delayed]);

    useEffect(() => {
      setInput(value);
    }, [value]);

    return (
      <Autocomplete
        ref={ref}
        value={input}
        onChange={setInput}
        sx={(theme, u) => ({
          height: '100%',
          width: props.width,
          input: {
            '&:focus': {
              boxShadow: `0px 0px 0px 2px ${
                theme.colors[props.error ? 'red' : theme.primaryColor][4]
              }40`,
              borderColor: props.error
                ? theme.colors.red[6]
                : theme.colors[theme.primaryColor][6],
            },
          },
          ".mantine-Input-section[data-position='right']": {
            pointerEvents: 'initial',
          },
          ...(typeof sx === 'function' ? (sx(theme, u) as any) : sx),
        })}
        radius="md"
        placeholder={placeholder}
        leftSection={
          loadingQuery ? <LoadingIndicator size="xs" /> : leftSection
        }
        rightSection={loadingMutation && <LoadingIndicator size="xs" />}
        name={props.name}
        comboboxProps={{
          shadow: 'md',
        }}
        {...props}
      />
    );
  }
);
